import { lazy, FC, Suspense, useEffect } from 'react';
import {
  Route,
  Navigate,
  // createHashRouter,
  createRoutesFromElements,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'shared/helpers/useAppDispatch/useAppDispatch';
import GameLayoutRoute from 'shared/layouts/gameLayoutRoute';
import MainLayoutRoute from 'shared/layouts/mainLayoutRoute';
import LoginLayoutRoute from 'shared/layouts/loginLayoutRoute';
import WebLoginLayoutRoute from 'shared/layouts/webLoginLayoutRoute';
import PollsLayoutRoute from 'shared/layouts/pollsLayout';
import ROUTES from 'shared/constants/routes';
import { getIsAuth } from './login/store/selectors/loginSelectors';
import { loginActions } from './login/store/store';
import { ownRespondentInviteActions } from './invitePage/model/slice/ownRespondentInviteSlice';
import 'assets/style/style.scss';
import { getEnv } from 'store/appSelector';
import { getAuthUserData } from 'shared/helpers/auth';
import { getMaxInitDataUnsafe } from 'shared/helpers/max/maxBridge';

const TestsDemoPage = lazy(() => import('pages/Splittest/tests/demo'));
const TestsDemoItemPage = lazy(() => import('pages/Splittest/tests/demo/item'));

const BalancePage = lazy(() => import('pages/Splittest/balance'));
const ProfilePage = lazy(() => import('pages/Splittest/profile'));
const ProfileInformationPage = lazy(
  () => import('pages/Splittest/profile/profileInformation'),
);
const ProfileQuestionnairePage = lazy(
  () => import('pages/Splittest/profile/profileQuestionnaire'),
);
const ProfileGradePage = lazy(
  () => import('pages/Splittest/profile/profileGrade'),
);
const ProfileFriendsPage = lazy(
  () => import('pages/Splittest/profile/profileFriends'),
);
const MorePage = lazy(() => import('pages/Splittest/more'));
const RegistrationPage = lazy(() => import('pages/login/registration'));
const TasksPage = lazy(() => import('pages/Splittest/tasks'));
const TestsPage = lazy(() => import('pages/Splittest/tests'));
const GamesPage = lazy(() => import('pages/Splittest/games'));
const PlaysPage = lazy(() => import('pages/RockPaperScissors/plays'));
const LeadersPage = lazy(() => import('pages/RockPaperScissors/leaders'));
const FriendsPage = lazy(() => import('pages/RockPaperScissors/friends'));
const ShopPage = lazy(() => import('pages/RockPaperScissors/shops'));
const SettingsPage = lazy(() => import('pages/RockPaperScissors/settings'));
const LoginPage = lazy(() => import('pages/login'));
const WebLoginPage = lazy(() => import('pages/webLogin'));
const StartPage = lazy(() => import('pages/RockPaperScissors/start'));
const InvitePage = lazy(() => import('pages/invitePage/InvitePage'));
const PollsPageDetails = lazy(
  () => import('pages/pollsPage/ui/pollDetailsPage'),
);

const Routing: FC = () => {
  const dispatch = useAppDispatch();
  const isReduxAuth = useSelector(getIsAuth);
  const isStoreAuth = getAuthUserData();
  const env = useSelector(getEnv); // 'telegram' | 'browser' | 'native_webview' | null
  const isAuth =
    env === 'telegram' || env === 'max' ? isReduxAuth : !!isStoreAuth;

  const search = document.location.search;
  const tgWebAppStartParam = new URLSearchParams(search).get(
    'tgWebAppStartParam',
  );
  const startapp = new URLSearchParams(search).get('startapp');

  useEffect(() => {
    if (startapp && startapp.includes('poll_id')) {
      dispatch(loginActions.setPollId(startapp.replace('poll_id', '')));
    }
    if (tgWebAppStartParam && tgWebAppStartParam.includes('poll_id')) {
      dispatch(
        loginActions.setPollId(tgWebAppStartParam.replace('poll_id', '')),
      );
    }
    if (startapp && startapp.includes('orlh')) {
      dispatch(
        ownRespondentInviteActions.setInviteHash(startapp.replace('orlh', '')),
      );
    }
    if (tgWebAppStartParam && tgWebAppStartParam.includes('orlh')) {
      dispatch(
        ownRespondentInviteActions.setInviteHash(
          tgWebAppStartParam.replace('orlh', ''),
        ),
      );
    }
    if (
      tgWebAppStartParam &&
      !tgWebAppStartParam.includes('poll_id') &&
      !tgWebAppStartParam.includes('orlh')
    ) {
      dispatch(loginActions.setRefferal(tgWebAppStartParam || undefined));
    }

    if (env === 'max') {
      const maxSp = getMaxInitDataUnsafe()?.start_param;
      if (maxSp?.includes('poll_id')) {
        dispatch(loginActions.setPollId(maxSp.replace('poll_id', '')));
      }
      if (maxSp?.includes('orlh')) {
        dispatch(
          ownRespondentInviteActions.setInviteHash(maxSp.replace('orlh', '')),
        );
      }
      if (maxSp && !maxSp.includes('poll_id') && !maxSp.includes('orlh')) {
        dispatch(loginActions.setRefferal(maxSp));
      }
    }
  }, [dispatch, startapp, tgWebAppStartParam, env]);

  const getUnauthRedirect = () => {
    // env может быть null, если bootstrap ещё не выставил.
    // но у вас bootstrap должен оборачивать Routing, поэтому env уже не null.
    if (env === 'browser') return ROUTES.WEB_LOGIN.ROUTE;
    if (env === 'telegram' || env === 'max') return ROUTES.LOGIN.ROUTE;
    return ROUTES.LOGIN.ROUTE; // по умолчанию
  };

  // console.log('refId', refId);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<GameLayoutRoute isAuth={isAuth} />}>
          <Route
            path={ROUTES.PLAY.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <PlaysPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.LEADERS.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <LeadersPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.FRIENDS.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <FriendsPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.SHOP.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <ShopPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.SETTINGS.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.START.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <StartPage />
              </Suspense>
            }
          />
        </Route>

        <Route element={<MainLayoutRoute isAuth={isAuth} />}>
          <Route
            path={ROUTES.MORE.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <MorePage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.PROFILE.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <ProfilePage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.BALANCE.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <BalancePage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.PROFILE_INFORMATION.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <ProfileInformationPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.PROFILE_QUESTIONNAIRE.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <ProfileQuestionnairePage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.PROFILE_GRADE.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <ProfileGradePage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.PROFILE_FRIENDS.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <ProfileFriendsPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.REGISTRATION.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <RegistrationPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.TESTS_DEMO.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <TestsDemoPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.TESTS_DEMO_ITEM.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <TestsDemoItemPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.TASKS.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <TasksPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.TESTS.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <TestsPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.GAMES.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <GamesPage />
              </Suspense>
            }
          />
        </Route>

        <Route element={<LoginLayoutRoute isAuth={isAuth} />}>
          <Route
            path={ROUTES.LOGIN.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <LoginPage />
              </Suspense>
            }
          />
        </Route>

        <Route element={<PollsLayoutRoute isAuth={isAuth} />}>
          <Route
            path={ROUTES.POLL.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <PollsPageDetails />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.INVITE.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }}></div>}>
                <InvitePage />
              </Suspense>
            }
          />
        </Route>

        <Route element={<WebLoginLayoutRoute isAuth={isAuth} />}>
          <Route
            path={ROUTES.WEB_LOGIN.ROUTE}
            element={
              <Suspense fallback={<div style={{ margin: 'auto' }} />}>
                <WebLoginPage />
              </Suspense>
            }
          />
        </Route>

        <Route
          path="*"
          element={
            <Navigate
              to={isAuth ? ROUTES.TESTS.ROUTE : getUnauthRedirect()}
              replace
            />
          }
        />
      </>,
    ),
    // { basename: '/games' },
  );

  return (
    <>
      <RouterProvider router={router} />
    </>
  );
};

export default Routing;
